/**
 * @component ry-list
 * @category  display
 * @summary   Styled vertical (or horizontal) list (Fomantic UI `.ui.list` equivalent). Direct children become items — wrap content however you like inside each item. Layout variants are pure attribute hooks; ARIA semantics auto-apply: `[ordered]` becomes `role="list"` with each child `role="listitem"`, and an ordered list also gets a numbered prefix on each item.
 *
 * Reference: Fomantic UI's list element. ry-list focuses on the layout +
 * variant scaffolding; bullet glyphs, dividers, and indent are pure CSS.
 * For form-like checklists, use stacked `<ry-checkbox>` instead.
 *
 * @attribute ordered      boolean   default=false   When true, the list is presented as ordered (numbered) and items render a `1.`, `2.`, ... prefix via the list's CSS counter.
 * @attribute horizontal   boolean   default=false   Renders items in a horizontal row instead of a vertical column.
 * @attribute divided      boolean   default=false   Adds a divider rule between adjacent items (vertical OR horizontal depending on layout).
 * @attribute bulleted     boolean   default=false   Renders a bullet glyph before each item. Mutually exclusive with [ordered] (consumer choice; component doesn't enforce).
 * @attribute relaxed      boolean   default=false   Adds extra space between items.
 * @attribute celled       boolean   default=false   Adds a border around each item, like a cell.
 * @attribute size         'sm' | 'lg'?              Pure CSS hook for compact / enlarged list text.
 *
 * @content   Direct children become items. Each is tagged with `data-ry-target="item"` and `role="listitem"` (when the list itself has role="list" applied). Children can be `<div>`, `<a>`, `<li>` — anything that makes semantic sense for your content.
 *
 * @dom
 *   ry-list[ordered?][horizontal?][divided?][bulleted?][relaxed?][celled?][size?]   role="list"
 *     > {child 1}    data-ry-target="item"   role="listitem"
 *     > {child 2}    data-ry-target="item"   role="listitem"
 *     > ...
 *
 * @gotcha  Every direct child is treated as a list item. If you wrap items in extra `<div>` containers, the wrapper becomes the item — adjust nesting accordingly. Use a child element that is semantically appropriate (`<a>` for navigation lists, `<li>` for raw lists, etc.).
 *
 * @gotcha  `[ordered]` and `[bulleted]` are technically independent — you can set both and the consumer chooses via CSS which marker to show. The default ry-ui theme privileges `[ordered]` numbers when both are set, but this is theme behavior, not enforced by the JS.
 *
 * @gotcha  Setup-once: dynamically inserted children after mount do NOT get `data-ry-target="item"` or `role="listitem"`. Re-render or call `el.setup()` after mutation — or use `<ry-list>` as a target for `MutationObserver` in your own code.
 *
 * @gotcha  ARIA `role="list"` on the host can suppress the implicit semantics of `<ul>` / `<ol>` if you wrap them inside `<ry-list>`. By design, ry-list always asserts `role="list"` and `role="listitem"` on its children regardless of underlying tag — this is what gives consistent semantics across `<div>`, `<a>`, `<li>` children.
 *
 * @example basic
 * ```html
 * <ry-list>
 *   <div>Apples</div>
 *   <div>Bananas</div>
 *   <div>Cherries</div>
 * </ry-list>
 * ```
 *
 * @example ordered
 * ```html
 * <ry-list ordered>
 *   <div>First step</div>
 *   <div>Second step</div>
 *   <div>Third step</div>
 * </ry-list>
 * ```
 *
 * @example bulleted
 * ```html
 * <ry-list bulleted>
 *   <div>Feature A</div>
 *   <div>Feature B</div>
 *   <div>Feature C</div>
 * </ry-list>
 * ```
 *
 * @example horizontal
 * ```html
 * <ry-list horizontal>
 *   <a href="/">Home</a>
 *   <a href="/about">About</a>
 *   <a href="/contact">Contact</a>
 * </ry-list>
 * ```
 *
 * @example divided
 * ```html
 * <ry-list divided>
 *   <div>Section 1</div>
 *   <div>Section 2</div>
 *   <div>Section 3</div>
 * </ry-list>
 * ```
 *
 * @example horizontal-divided
 * ```html
 * <ry-list horizontal divided>
 *   <a href="/">Home</a>
 *   <a href="/docs">Docs</a>
 *   <a href="/blog">Blog</a>
 * </ry-list>
 * ```
 *
 * @example relaxed-celled
 * ```html
 * <ry-list relaxed celled>
 *   <div>Padded cell A</div>
 *   <div>Padded cell B</div>
 * </ry-list>
 * ```
 *
 * @tutorial mount-tags-children-as-items
 * ```js
 * const el = mount(`
 *   <ry-list>
 *     <div>One</div>
 *     <div>Two</div>
 *     <div>Three</div>
 *   </ry-list>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 3, 'every direct child tagged as an item');
 * ```
 *
 * @tutorial role-list-and-listitem-applied
 * ```js
 * // Wrong assumption: ARIA roles only auto-apply when ordered. ry-list
 * // ALWAYS applies role="list" + role="listitem" for consistent semantics
 * // regardless of underlying tag.
 * const el = mount(`
 *   <ry-list>
 *     <div>One</div>
 *     <a href="/">Two</a>
 *   </ry-list>
 * `);
 * await expectAttr(el, 'role', 'list');
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectAttr(items[0], 'role', 'listitem', 'div child gets role="listitem"');
 * await expectAttr(items[1], 'role', 'listitem', 'a child also gets role="listitem"');
 * ```
 *
 * @tutorial attribute-hooks-survive
 * ```js
 * const el = mount('<ry-list ordered horizontal divided relaxed celled size="lg"><div>x</div></ry-list>');
 * await expectEqual(el.hasAttribute('ordered'), true);
 * await expectEqual(el.hasAttribute('horizontal'), true);
 * await expectEqual(el.hasAttribute('divided'), true);
 * await expectEqual(el.hasAttribute('relaxed'), true);
 * await expectEqual(el.hasAttribute('celled'), true);
 * await expectAttr(el, 'size', 'lg');
 * ```
 *
 * @tutorial empty-list-is-graceful
 * ```js
 * // Wrong assumption: empty list throws or warns. It silently mounts as
 * // an empty role="list" container.
 * const el = mount('<ry-list></ry-list>');
 * await expectAttr(el, 'role', 'list');
 * await expectEqual(el.querySelectorAll('[data-ry-target="item"]').length, 0, 'no items');
 * ```
 *
 * @tutorial accepts-mixed-child-element-types
 * ```js
 * const el = mount(`
 *   <ry-list>
 *     <div>Div item</div>
 *     <a href="/">Anchor item</a>
 *     <span>Span item</span>
 *   </ry-list>
 * `);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 3, 'all children become items regardless of tag');
 * await expectEqual(items[0].tagName, 'DIV');
 * await expectEqual(items[1].tagName, 'A');
 * await expectEqual(items[2].tagName, 'SPAN');
 * ```
 *
 * @tutorial preserves-explicit-role-on-host
 * ```js
 * // If a consumer sets role on the host, ry-list doesn't override.
 * const el = mount('<ry-list role="menu"><div>x</div></ry-list>');
 * await expectAttr(el, 'role', 'menu', 'explicit role survives');
 * ```
 *
 * @see ry-breadcrumb
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyList extends RyElement {
  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'list');
    Array.from(this.children).forEach((child) => {
      const el = child as HTMLElement;
      if (!el.hasAttribute('data-ry-target')) el.setAttribute('data-ry-target', 'item');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'listitem');
    });
  }
}

customElements.define('ry-list', RyList);
